import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { History, RotateCcw, Loader2, ChevronDown, ChevronRight } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const show = (v) => {
  if (v === undefined || v === null || v === "") return "(empty)";
  const s = typeof v === "string" ? v : JSON.stringify(v, null, 1);
  return s.length > 600 ? `${s.slice(0, 600)}…` : s;
};

function OpDiff({ op }) {
  const fields = op.kind === "update" ? Object.keys(op.after || {}) : [op.kind === "insert" ? "added" : "removed"];
  return fields.map((f) => (
    <div key={f} className="grid sm:grid-cols-[140px_1fr_1fr] gap-2 text-xs py-1.5 border-b border-[#F0EFEC] last:border-0">
      <div className="font-semibold text-[#575755]">{op.coll === "seo_redirects" ? `redirect ${op.filter?.from_path}` : f}</div>
      <pre className="whitespace-pre-wrap break-words bg-[#FBEDED] text-[#7A2323] rounded px-2 py-1">{op.kind === "insert" ? "(none)" : show(op.kind === "delete" ? op.before?.to : op.before?.[f])}</pre>
      <pre className="whitespace-pre-wrap break-words bg-[#E7F0EA] text-[#2E5C47] rounded px-2 py-1">{op.kind === "delete" ? "(removed)" : show(op.kind === "insert" ? op.after?.to : op.after?.[f])}</pre>
    </div>
  ));
}

function ChangeRow({ c, onRevert, busy }) {
  const [open, setOpen] = useState(false);
  const isRevert = c.tool === "revert_seo_change";
  return (
    <div data-testid={`seo-change-${c.id}`} className="border-b border-[#F0EFEC] last:border-0">
      <div className="flex flex-wrap items-center gap-3 px-6 py-4">
        <button onClick={() => setOpen(!open)} disabled={!c.ops?.length} data-testid={`seo-change-toggle-${c.id}`} className="text-[#8a8a86] hover:text-[#1F3C88] disabled:opacity-30">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm text-[#0A0A0A]">{c.summary}</div>
          <div className="text-xs text-[#8a8a86] mt-0.5">{fmt(c.at)} · {c.key_label || "Unknown"} · <span className="font-mono">{c.tool}</span></div>
        </div>
        {isRevert ? <span className="text-xs text-[#8a8a86]">Revert action</span>
          : c.reverted ? <span data-testid={`seo-change-reverted-${c.id}`} className="px-2.5 py-1 rounded-full bg-[#F0EFEC] text-[#575755] text-xs font-medium">Reverted {fmt(c.reverted_at)}</span>
          : (
            <button data-testid={`seo-change-revert-${c.id}`} disabled={busy} onClick={() => onRevert(c)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#E5E5E2] text-xs font-semibold text-[#992B2B] hover:bg-[#FBEDED] disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Revert
            </button>
          )}
      </div>
      {open && (
        <div className="px-6 pb-4 pl-[52px]" data-testid={`seo-change-diff-${c.id}`}>
          <div className="hidden sm:grid grid-cols-[140px_1fr_1fr] gap-2 text-[10px] uppercase tracking-[0.12em] text-[#8a8a86] mb-1"><span>Field</span><span>Before</span><span>After</span></div>
          {c.ops.map((op, i) => <OpDiff key={i} op={op} />)}
        </div>
      )}
    </div>
  );
}

export function SeoChangeHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/seo-changes")).data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revert = async (c, force = false) => {
    setBusyId(c.id);
    try {
      await api.post(`/admin/seo-changes/${c.id}/revert`, null, { params: force ? { force: true } : {} });
      toast.success("Change reverted");
      load();
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      if (!force && /newer change/i.test(msg)) toast.error(msg, { action: { label: "Revert anyway", onClick: () => revert(c, true) } });
      else toast.error(msg);
    } finally { setBusyId(null); }
  };

  return (
    <div className="bg-white border border-[#E5E5E2] rounded-sm mt-8" data-testid="seo-change-history">
      <div className="px-6 py-5 border-b border-[#E5E5E2] flex items-center gap-2">
        <History className="w-5 h-5 text-[#1F3C88]" />
        <div>
          <h3 className="font-serif text-lg text-[#0A0A0A]">SEO Change History</h3>
          <p className="text-xs text-[#8a8a86]">Every SEO change made through the API or Agent Page settings. Expand a row to see before/after.</p>
        </div>
      </div>
      {loading ? <div className="py-12 text-center text-sm text-[#9a978f]">Loading…</div>
        : rows.length === 0 ? <div data-testid="seo-change-empty" className="py-12 text-center text-sm text-[#9a978f]">No SEO changes yet.</div>
        : <div className="max-h-[640px] overflow-y-auto">{rows.map((c) => <ChangeRow key={c.id} c={c} busy={busyId === c.id} onRevert={revert} />)}</div>}
    </div>
  );
}
